import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import Loader from '@jetbrains/ring-ui/components/loader/loader'
import classNames from 'classnames'
import plur from 'plur'
import type {SyntheticEvent} from 'react'
import * as React from 'react'
import {useState} from 'react'
import type {PreloadedQuery, UseQueryLoaderLoadQueryOptions} from 'react-relay'
import {usePreloadedQuery, useQueryLoader} from 'react-relay'

import type {AgentPoolId} from '../../../../../types'
import {ROOT_PROJECT_ID} from '../../../../../types'
import {internalProps} from '../../../../../types/BS_types'
import {emptyArray} from '../../../../../utils/empty'
import {subscribeOnOverallEvents} from '../../../../../utils/subscriber'
import {TreeEventType} from '../../../../common/Sidebar/Sidebar.types'
import {keyboardToTreeEvent} from '../../../../common/Sidebar/Sidebar.utils'
import {
  OVERALL_AGENT_POOL_EVENT_TOPICS,
  OVERALL_PROJECT_EVENT_TOPICS,
} from '../../AgentsPages.actions'
import type {
  AgentPoolPageAgentPoolProjectsQuery,
  AgentPoolPageAgentPoolProjectsQuery$data,
} from '../__generated__/AgentPoolPageAgentPoolProjectsQuery.graphql'
import {PROJECT_AGENT_POOL_TYPENAME} from '../AgentPoolPage.constants'
import {SEARCHABLE_PROJECTS_NUMBER, useTreeState} from '../AgentPoolPage.hooks'
import {agentPoolProjectsTreeQuery} from '../AgentPoolPage.queries'
import {createProjectsTree, getFlatProjectsTree} from '../AgentPoolPage.selectors'
import type {ProjectsConnectionEdges} from '../AgentPoolPage.types'
import {matchName} from '../AgentPoolPage.utils'
import AgentPoolTabToolbar from '../AgentPoolTabToolbar/AgentPoolTabToolbar'

import AssignProjectsButton from './AssignProjectsButton/AssignProjectsButton'
import {DiassociateProjectButton} from './DiassociateProjectButton/DiassociateProjectButton'
import LeanFilter from './LeanFilter/LeanFilter'
import ProjectNameCell from './ProjectNameCell/ProjectNameCell'
import ProjectPoolsCell from './ProjectPoolsCell/ProjectPoolsCell'
import ProjectRow from './ProjectRow/ProjectRow'
import {useUnassignedProjectsState} from './ProjectsTab.hooks'
import UndoAssignProjectButton from './UndoDiassociateProjectButton/UndoDiassociateProjectButton'

import styles from './ProjectsTab.css'

type Props = {
  agentPoolId: AgentPoolId
  canAssignProjects: boolean
  className?: string
}
const STRING_ROOT_PROJECT_ID = '_Root'
const SEARCH_INPUT_ID = 'PROJECTS_TAB_SEARCH'

function ProjectsTab({
  poolProjectsQueryRef,
  agentPoolId,
  canAssignProjects,
  className,
}: Props & {
  poolProjectsQueryRef: PreloadedQuery<AgentPoolPageAgentPoolProjectsQuery>
}): React.ReactElement | null {
  const [showArchived, setShowArchived] = React.useState(false)
  const {agentPool}: AgentPoolPageAgentPoolProjectsQuery$data = usePreloadedQuery(
    agentPoolProjectsTreeQuery,
    poolProjectsQueryRef,
  )
  const isProjectPool = agentPool?.__typename === PROJECT_AGENT_POOL_TYPENAME
  const projectEdges = agentPool?.projects?.edges || emptyArray
  const excludedCount = agentPool?.projects?.excludedCount || 0
  const agentPoolRawId = String(agentPoolId)
  const handleShowArchived = React.useCallback(() => {
    setShowArchived(!showArchived)
  }, [showArchived, setShowArchived])
  const [expandState, setExpandState, searchQuery, setSearchQuery] = useTreeState({
    [STRING_ROOT_PROJECT_ID]: true,
  })
  const onSearchChange = React.useCallback(
    (e: SyntheticEvent<HTMLInputElement>) => setSearchQuery(e.currentTarget.value),
    [setSearchQuery],
  )
  const onSearchClear = React.useCallback(() => setSearchQuery(''), [setSearchQuery])

  const [unassignedProjectEdgesHash, persistUnassignedProjectEdge, cleanupUnassignedProjectEdge] =
    useUnassignedProjectsState(agentPoolRawId, projectEdges)

  const archivedProjectsNumber = React.useMemo(
    () =>
      projectEdges.reduce((number, {node: {archived}}) => number + (archived === true ? 1 : 0), 0),
    [projectEdges],
  )

  const unassignedProjectEdges = React.useMemo(
    () =>
      Object.values(unassignedProjectEdgesHash).map(
        edgeState => edgeState?.edge,
      ) as ProjectsConnectionEdges,
    [unassignedProjectEdgesHash],
  )

  const flatTree = React.useMemo(
    () =>
      getFlatProjectsTree(
        createProjectsTree([...unassignedProjectEdges, ...projectEdges]),
        expandState,
        agentPoolRawId,
        (item, parentIsMatching) =>
          (showArchived || !item.archived) &&
          (parentIsMatching || matchName(item.name, searchQuery)),
      ).map(treeItem => ({
        ...treeItem,
        focusable:
          treeItem.rawId !== ROOT_PROJECT_ID &&
          (treeItem.assigned || unassignedProjectEdgesHash[treeItem.rawId] != null) &&
          (showArchived || !treeItem.archived),
      })),
    [
      unassignedProjectEdges,
      projectEdges,
      expandState,
      agentPoolRawId,
      showArchived,
      searchQuery,
      unassignedProjectEdgesHash,
    ],
  )

  const treeItemsNumber = projectEdges.length + unassignedProjectEdges.length

  const showAssignProjectsButton =
    internalProps['teamcity.ui.newAgentPoolManagement'] !== false && canAssignProjects
  const showFilter = treeItemsNumber > SEARCHABLE_PROJECTS_NUMBER
  const showArchivedCheckbox = archivedProjectsNumber > 0
  const showToolbar = showAssignProjectsButton || showFilter || showArchivedCheckbox
  const [focused, setFocused] = useState<string>()

  return (
    <div className={className}>
      {showToolbar ? (
        <AgentPoolTabToolbar>
          {showAssignProjectsButton && agentPool?.name != null ? (
            <AssignProjectsButton
              agentPoolId={agentPoolId}
              agentPoolName={agentPool.name}
              className={styles.assignButton}
            />
          ) : null}
          {showFilter ? (
            <LeanFilter
              onChange={onSearchChange}
              onClear={onSearchClear}
              query={searchQuery}
              id={SEARCH_INPUT_ID}
              className={styles.filterButton}
            />
          ) : null}
          {showArchivedCheckbox ? (
            <Checkbox checked={showArchived} onChange={handleShowArchived}>
              {`Show ${archivedProjectsNumber} archived ${plur('project', archivedProjectsNumber)}`}
            </Checkbox>
          ) : null}
        </AgentPoolTabToolbar>
      ) : null}
      {treeItemsNumber > 0 ? (
        <div role="treegrid" aria-label="Projects">
          <div role="row" className={classNames(styles.tableRow, styles.tableHeader)}>
            <div role="columnheader" className={classNames(styles.tableCell, styles.nameTableCell)}>
              <span className={styles.nameHeader}>{'Name'}</span>
            </div>
            <div
              role="columnheader"
              className={classNames(styles.tableCell, styles.projectsTableCell)}
            >
              {'Also in pools'}
            </div>
          </div>
          {flatTree.map((treeItem, i) => {
            const persistedItemState = unassignedProjectEdgesHash[treeItem.rawId]

            if (!treeItem.focusable) {
              return null
            }

            const canUnassign =
              !isProjectPool &&
              treeItem.permissions != null &&
              treeItem.permissions.manageAgentPoolsForProject === true

            return (
              <ProjectRow
                key={treeItem.rawId}
                projectItem={treeItem}
                onToggleExpand={setExpandState}
                focused={focused === treeItem.rawId}
                onKeyDown={e => {
                  const treeEvent = keyboardToTreeEvent(e, flatTree, i)
                  switch (treeEvent?.type) {
                    case TreeEventType.EVENT_TYPE_EXPAND:
                      setExpandState(treeItem.rawId, true)
                      e.stopPropagation()
                      break
                    case TreeEventType.EVENT_TYPE_COLLAPSE:
                      setExpandState(treeItem.rawId, false)
                      e.stopPropagation()
                      break
                    case TreeEventType.EVENT_TYPE_FOCUS:
                      setFocused(flatTree[treeEvent.index]?.rawId)
                      e.stopPropagation()
                      break
                    default:
                  }
                }}
              >
                <ProjectNameCell
                  className={classNames(styles.tableCell, styles.nameTableCell)}
                  searchQuery={searchQuery}
                  showArchived={showArchived}
                  projectItem={treeItem}
                />
                <ProjectPoolsCell
                  className={classNames(styles.tableCell, styles.projectsTableCell)}
                  agentPoolRawId={agentPoolRawId}
                  {...treeItem}
                  actions={
                    internalProps['teamcity.ui.newAgentPoolManagement'] !== false ? (
                      persistedItemState != null ? (
                        <UndoAssignProjectButton
                          agentPoolRawId={agentPoolRawId}
                          unassignedProjectItem={persistedItemState.item}
                          recursively={persistedItemState.recursively}
                          onCompleted={projectRawIds => {
                            projectRawIds.forEach(projectRawId =>
                              cleanupUnassignedProjectEdge(projectRawId),
                            )
                          }}
                        />
                      ) : canUnassign ? (
                        <DiassociateProjectButton
                          onCompleted={persistUnassignedProjectEdge}
                          agentPoolRawId={agentPoolRawId}
                          projectItem={treeItem}
                        />
                      ) : null
                    ) : null
                  }
                />
              </ProjectRow>
            )
          })}
        </div>
      ) : null}
      {excludedCount > 0 ? (
        <div className={styles.excludedProjectsNumber}>
          {`${excludedCount} ${plur(
            'project is',
            'projects are',
            excludedCount,
          )} hidden as you don't have permissions to see ${plur('it', 'them', excludedCount)}`}
        </div>
      ) : null}
      {treeItemsNumber === 0 && excludedCount === 0 ? <div>{'No assigned projects'}</div> : null}
    </div>
  )
}

const fetchOptions: UseQueryLoaderLoadQueryOptions = {fetchPolicy: 'store-and-network'}

const PROJECTS_TAB_TOPICS = OVERALL_AGENT_POOL_EVENT_TOPICS.concat(OVERALL_PROJECT_EVENT_TOPICS)

function ProjectsTabContainer(props: Props): React.ReactElement | null {
  const {agentPoolId} = props
  const [projectsTreeQueryRef, loadProjectsTreeQuery, disposeProjectsTreeQuery] =
    useQueryLoader<AgentPoolPageAgentPoolProjectsQuery>(agentPoolProjectsTreeQuery)
  const variables = React.useMemo(() => ({agentPoolId: String(agentPoolId)}), [agentPoolId])

  React.useEffect(
    () =>
      subscribeOnOverallEvents(PROJECTS_TAB_TOPICS, () =>
        loadProjectsTreeQuery(variables, fetchOptions),
      ),
    [agentPoolId, loadProjectsTreeQuery, variables],
  )
  React.useEffect(() => disposeProjectsTreeQuery, [disposeProjectsTreeQuery])
  return projectsTreeQueryRef != null ? (
    <React.Suspense fallback={<Loader />}>
      <ProjectsTab poolProjectsQueryRef={projectsTreeQueryRef} {...props} />
    </React.Suspense>
  ) : null
}

export default React.memo(ProjectsTabContainer)
